/**
 * react-query wiring for `/api/teacher/*` (brief §teacher). Query keys are
 * namespaced `["teacher", …]` per `docs/CONVENTIONS.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@client/api/client";
import type { ErrorCode } from "@shared/api";
import type {
  QuestionReport,
  QuestionStats,
  ReportStatus,
  StudentDetail,
  StudentRoleFilter,
  StudentSort,
  StudentSummary,
  TeacherExamsOverview,
  TeacherOverview,
  TeacherReleases,
  LectureReleaseChange,
} from "./types";

export const teacherKeys = {
  all: ["teacher"] as const,
  overview: () => ["teacher", "overview"] as const,
  students: (sort: StudentSort, q: string, role: StudentRoleFilter) =>
    ["teacher", "students", sort, q, role] as const,
  student: (id: string) => ["teacher", "students", id] as const,
  questions: (chapter: number | undefined, type: string | undefined) =>
    ["teacher", "questions", chapter ?? "all", type ?? "all"] as const,
  exams: () => ["teacher", "exams"] as const,
  reportsAll: () => ["teacher", "reports"] as const,
  reports: (status: ReportStatus | undefined) => ["teacher", "reports", status ?? "all"] as const,
  releases: () => ["teacher", "releases"] as const,
};

export function useTeacherOverview() {
  return useQuery({
    queryKey: teacherKeys.overview(),
    queryFn: () => api.get<TeacherOverview>("/api/teacher/overview"),
  });
}

export function useTeacherStudents(sort: StudentSort, q: string, role: StudentRoleFilter) {
  return useQuery({
    queryKey: teacherKeys.students(sort, q, role),
    queryFn: () => {
      const params = new URLSearchParams({ sort, role });
      if (q.trim()) params.set("q", q.trim());
      return api.get<StudentSummary[]>(`/api/teacher/students?${params.toString()}`);
    },
  });
}

export function useTeacherStudent(id: string | undefined) {
  return useQuery({
    queryKey: teacherKeys.student(id ?? ""),
    queryFn: () => api.get<StudentDetail>(`/api/teacher/students/${encodeURIComponent(id!)}`),
    enabled: !!id,
  });
}

export function useTeacherQuestions(chapter: number | undefined, type: string | undefined) {
  return useQuery({
    queryKey: teacherKeys.questions(chapter, type),
    queryFn: () => {
      const params = new URLSearchParams({ sort: "hardest" });
      if (chapter !== undefined) params.set("chapter", String(chapter));
      if (type) params.set("type", type);
      return api.get<QuestionStats[]>(`/api/teacher/questions?${params.toString()}`);
    },
  });
}

export function useTeacherExams() {
  return useQuery({
    queryKey: teacherKeys.exams(),
    queryFn: () => api.get<TeacherExamsOverview>("/api/teacher/exams"),
  });
}

export function useTeacherReports(status: ReportStatus | undefined) {
  return useQuery({
    queryKey: teacherKeys.reports(status),
    queryFn: () => {
      const params = status ? `?status=${status}` : "";
      return api.get<QuestionReport[]>(`/api/teacher/reports${params}`);
    },
  });
}

export function useResolveReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ report: QuestionReport }>(`/api/teacher/reports/${id}/resolve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: teacherKeys.reportsAll() }),
  });
}

export function useChangeRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; role: "student" | "teacher" }) =>
      api.post<{ user: { id: string; role: string } }>(`/api/teacher/users/${input.id}/role`, {
        role: input.role,
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: teacherKeys.student(variables.id) });
      qc.invalidateQueries({ queryKey: ["teacher", "students"] });
    },
  });
}

/** Pages are fetched one request at a time; a server that never ends the chain must not loop forever. */
const MAX_EXPORT_PAGES = 1000;

/** A failed export request as the `ApiError` the rest of the client (`messageForError`) understands. */
async function exportError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as {
      ok?: boolean;
      error?: { code?: ErrorCode; message?: string; requestId?: string };
    };
    if (body.ok === false && body.error?.code) {
      return new ApiError({
        code: body.error.code,
        message: body.error.message ?? `Export failed (${res.status})`,
        status: res.status,
        ...(body.error.requestId ? { requestId: body.error.requestId } : {}),
      });
    }
  } catch {
    // Not the JSON envelope (a proxy error page): fall through to the generic error.
  }
  return new ApiError({
    code: "INTERNAL",
    message: `Export failed (${res.status})`,
    status: res.status,
  });
}

/**
 * Not `api.get` — the response is `text/csv`, not the JSON envelope. The server
 * sends one page per request and names the next in `X-Next-Cursor` (absent on the
 * last page; the header row is on the first only), so the pages concatenate to
 * the whole CSV. Downloads via an in-memory blob link once every page is in;
 * a failed page rejects and downloads nothing. `onProgress` gets the number of
 * attempt rows fetched so far.
 */
export async function downloadAttemptsCsv(onProgress?: (rows: number) => void): Promise<void> {
  const parts: string[] = [];
  let rows = 0;
  let cursor: string | null = null;
  for (let page = 0; page < MAX_EXPORT_PAGES; page++) {
    const url: string =
      "/api/teacher/export.csv" + (cursor ? `?cursor=${encodeURIComponent(cursor)}` : "");
    let res: Response;
    try {
      res = await fetch(url, { credentials: "include" });
    } catch {
      throw new ApiError({
        code: "INTERNAL",
        message: "Network error — check your connection.",
        status: 0,
      });
    }
    if (!res.ok) throw await exportError(res);
    const text = await res.text();
    parts.push(text);
    // Every line is a row except the header line on the first page.
    rows += Math.max(0, text.split("\n").length - 1 - (page === 0 ? 1 : 0));
    onProgress?.(rows);
    cursor = res.headers.get("x-next-cursor");
    if (!cursor) break;
  }
  if (cursor) throw new ApiError({ code: "INTERNAL", message: "Export did not end", status: 500 });

  const blob = new Blob(parts, { type: "text/csv;charset=utf-8" });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = "attempts.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

/* ------------------------- lecture release (P1-12) ------------------------- */

export function useTeacherReleases() {
  return useQuery({
    queryKey: teacherKeys.releases(),
    queryFn: () => api.get<TeacherReleases>("/api/teacher/releases"),
  });
}

/** After a change the course catalogue (home card, lecture list) must be refetched too. */
function useReleaseSaved() {
  const qc = useQueryClient();
  return (data: TeacherReleases) => {
    qc.setQueryData(teacherKeys.releases(), data);
    void qc.invalidateQueries({ queryKey: ["content", "course"] });
  };
}

export function useSetReleaseOffset() {
  const saved = useReleaseSaved();
  return useMutation({
    mutationFn: (offsetDays: number) =>
      api.patch<TeacherReleases>("/api/teacher/releases", { offsetDays }),
    onSuccess: saved,
  });
}

export function useSetLectureRelease() {
  const saved = useReleaseSaved();
  return useMutation({
    mutationFn: ({ lectureId, change }: { lectureId: string; change: LectureReleaseChange }) =>
      api.patch<TeacherReleases>(`/api/teacher/releases/${encodeURIComponent(lectureId)}`, change),
    onSuccess: saved,
  });
}
